import {
  EllipsedText,
  FlexRow,
  isValidImage,
  isValidLatitude,
  isValidLongitude,
  isValidVideo,
  MaterialIcon,
  MaterialIconName,
  useMoment,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { ParagraphXSmall } from 'baseui/typography';
import { CSSProperties, memo, useMemo } from 'react';
import { ListChildComponentProps } from 'react-window';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { MEDIA_POSITION_ORIGIN } from '../../../../constants/media';
import { useCamera } from '../../../../hooks/useCamera';
import { useStyles } from '../../../../hooks/useStyles';
import { gptAlarmTypeIconState, gptAlarmTypeState } from '../../../../storage/gptStates';
import {
  capturingState,
  capturingTimelineEventState,
  capturingTimelineMapEventState,
} from '../../../../storage/streaming';
import { StreamingTimelineEvent, Timeline } from '../../../../types/streaming';
import { getTimeBySeconds } from '../../../../utils/fields';
import SafeGptAlarmIcon from '../../../shared/SafeGptAlarmIcon';
import { StyledTooltip } from '../../../ui/StyledTooltip';

function CapturingVideoTrackItem({ data, index, style }: Readonly<ListChildComponentProps<Timeline>>) {
  const { theme, css } = useStyles();
  const record = data[index];
  const { zoomTo } = useCamera();
  const [capturingData, setCapturingData] = useRecoilState(capturingState);
  const setEvent = useSetRecoilState(capturingTimelineEventState);
  const isSelected = capturingData.selectedRecord === record.uuid;
  const setMapEvent = useSetRecoilState(capturingTimelineMapEventState);

  const onLocateEvent = (event: StreamingTimelineEvent) => {
    setMapEvent(event);
    if (isValidLatitude(event.latitude) && isValidLongitude(event.longitude)) {
      zoomTo({
        lat: event.latitude,
        lng: event.longitude,
      });
    }
  };

  const onEventInfo = (event: StreamingTimelineEvent) => {
    setEvent(event);
  };

  const onToggleSelectRecord = (uuid: string) => {
    setCapturingData((prev) => ({
      ...prev,
      selectedRecord: prev.selectedRecord === uuid ? undefined : uuid,
    }));
  };

  const filteredEvents = useMemo(() => {
    return record.events?.filter((event) => event.mediaOrigin !== MEDIA_POSITION_ORIGIN);
  }, [record.events]);

  return (
    <div style={style as CSSProperties}>
      <div
        key={index}
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          position: 'relative',
          padding: '25px 10px 25px 22px',
          transition: 'background 0.1s ease',
          background: isSelected ? '#B8B8B8' : undefined,
          cursor: 'pointer',
          borderRadius: '5px',
          ':hover': {
            backgroundColor: theme.colors.buttonSecondaryActive,
          },
        })}
        onMouseEnter={() => {
          // DO SOMETHING ON MOUSE ENTER
        }}
        onMouseLeave={() => {
          // DO SOMETHING ON MOUSE LEAVE
        }}
        onClick={() => {
          onToggleSelectRecord(record.uuid);
        }}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            borderLeft: `4px solid ${theme.colors.backgroundOverlayLight}`,
            position: 'relative',
            width: '100%',
          })}
        >
          {/* First point */}
          <TimelinePoint
            time={record.endTime}
            label={record.endLabel}
          />

          {/* Events */}
          {!!filteredEvents?.length && filteredEvents.length > 0 && (
            <ul
              className={css({
                paddingLeft: '1.2rem',
                paddingRight: 0,
                marginTop: 0,
                marginBottom: 0,
                marginLeft: '45px',
                marginRight: '10px',
                listStyle: 'none',
                position: 'relative',
              })}
            >
              {filteredEvents.map((e, i) => (
                <li key={`secondary-event-${i}`}>
                  <FlexRow
                    centered
                    gap={theme.sizing.scale0}
                  >
                    <div
                      className={css({
                        ...theme.typography.ParagraphXSmall,
                        flex: 1,
                      })}
                    >
                      <EllipsedText
                        inline
                        breakAll
                      >
                        {e.geoReference ?? 'Sin referencia geográfica'}
                      </EllipsedText>
                    </div>
                    <StyledTooltip
                      content="Información"
                      showArrow={false}
                      placement="top"
                      ignoreBoundary
                    >
                      <Button
                        size="mini"
                        shape="square"
                        onClick={(evt) => {
                          evt.stopPropagation();
                          onEventInfo(e);
                        }}
                        kind="tertiary"
                      >
                        <MaterialIcon
                          name="info"
                          size="14px"
                        />
                      </Button>
                    </StyledTooltip>
                    <StyledTooltip
                      content="Encontrar en el mapa"
                      showArrow={false}
                      placement="top"
                      ignoreBoundary
                    >
                      <Button
                        size="mini"
                        shape="square"
                        onClick={(evt) => {
                          evt.stopPropagation();
                          onLocateEvent(e);
                        }}
                        kind="tertiary"
                      >
                        <MaterialIcon
                          name="location_searching"
                          size="14px"
                        />
                      </Button>
                    </StyledTooltip>
                    <TimelineEventIcon event={e} />
                  </FlexRow>
                </li>
              ))}
              <li
                className={css({
                  ...theme.typography.ParagraphXSmall,
                  textAlign: 'center',
                  marginLeft: '-45px',
                })}
              >
                Velocidad promedio: {record.speed}km/h.
              </li>
            </ul>
          )}

          {!filteredEvents?.length && (
            <ParagraphXSmall
              className={css({
                textAlign: 'center',
                paddingLeft: '8px',
              })}
              margin={0}
            >
              Velocidad promedio: {record.speed}km/h.
            </ParagraphXSmall>
          )}

          {/* Duration and distance data */}
          {(record.duration || record.duration === 0) && (
            <div
              className={css({
                ...theme.typography.ParagraphXSmall,
                fontSize: '11px',
                position: 'absolute',
                top: '50%',
                left: '0px',
                transform: 'translateY(-50%) translateX(-50%) rotate(-90deg)',
                fontWeight: '600',
                letterSpacing: '0.05em',
                padding: '1px',
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
              })}
            >
              <span>{getTimeBySeconds(record.duration, true)}</span>
              {<span>{Number((record.distance / 1000).toFixed(2)).toString()} Km</span>}
            </div>
          )}

          {/* Second point */}
          <TimelinePoint
            time={record.startTime}
            label={record.startLabel}
            atBottom
          />
        </div>
      </div>
    </div>
  );
}

const TimelineEventIcon = memo(({ event }: { event: StreamingTimelineEvent }) => {
  const { theme, css } = useStyles();
  const alarmData = useRecoilValue(gptAlarmTypeState(event?.alarmType));
  const alarmIcon = useRecoilValue(gptAlarmTypeIconState(event?.alarmType));

  const eventIcon = ((): MaterialIconName => {
    if (event.alarmType && event.alarmType > -1) {
      return alarmIcon ? '' : 'warning';
    }
    if (event.key) {
      if (isValidImage(event.key)) {
        return 'image';
      }
      if (isValidVideo(event.key)) {
        return 'videocam';
      }
    }
    return 'place';
  })();

  return (
    <div
      className={css({
        position: 'absolute',
        left: 0,
        transform: 'translateX(-50%)',
        backgroundColor: alarmData?.color || theme.colors.backgroundInversePrimary,
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '22px',
        height: '22px',
        boxSizing: 'border-box',
      })}
    >
      {!eventIcon && alarmIcon && (
        <SafeGptAlarmIcon
          url={alarmIcon}
          size={theme.sizing.scale550}
          color={theme.colors.contentInversePrimary}
        />
      )}
      {!alarmIcon && (
        <MaterialIcon
          name={eventIcon}
          size={theme.sizing.scale550}
          color={theme.colors.contentInversePrimary}
        />
      )}
    </div>
  );
});

const TimelinePoint = memo(({ time, label, atBottom = false }: { time: number; label: string; atBottom?: boolean }) => {
  const { theme, css } = useStyles();
  const { formatDateFromTs, formatTimeFromTs } = useMoment();

  return (
    <div
      className={css({
        display: 'flex',
        minHeight: '30px',
        alignItems: 'center',
        width: '100%',
        position: 'relative',
        top: atBottom ? '16px' : '-16px',
      })}
    >
      <StyledTooltip
        content={formatDateFromTs(time, 'DD/MMMM/yyyy HH:mm:ss')}
        showArrow={false}
        placement="top"
        ignoreBoundary
      >
        <div
          className={css({
            ...theme.typography.HeadingXSmall,
            color: theme.colors.contentTertiary,
            width: '30px',
            height: '30px',
            backgroundColor: theme.colors.backgroundSecondary,
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            left: '-17px',
          })}
        >
          <MaterialIcon
            name="polyline"
            size="15px"
          />
        </div>
      </StyledTooltip>
      <div
        className={css({
          ...theme.typography.LabelSmall,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
        })}
      >
        <div
          className={css({
            width: '100%',
            display: 'flex',
          })}
        >
          <EllipsedText
            maxLines={2}
            breakAll
          >
            {atBottom && time ? (label ? label : `${formatTimeFromTs(time)}`) : label}
          </EllipsedText>
        </div>
      </div>
    </div>
  );
});

export default memo(CapturingVideoTrackItem);
